import _ from 'lodash';
import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';

import {
  BitbucketWorkspace,
  BitbucketWorkspaces,
  Product,
  ProductKeys,
  Sites,
  SubmitErrorType,
  WacSignupFormProps,
  WacSignupFormState
} from '../model';

import {
  getIsBitbucket,
  getIsBundleBbJsw,
  getIsBundleDevops
} from '@atlassiansox/bxpkit-core';
import OriginTracing from '@atlassiansox/origin-tracing';
import { messages } from '../messages/sign-up-form';

import {
  getOrgAdminVortexStatusCounts,
  sanitizeLastName
} from '../layouts/legacy/view/utility';
import { useAppSelector } from '../redux';
import { DEFAULT_ORG_RESPONSE } from './use-org-info';
import { AccountOrg } from '../layouts/legacy/view/utility';

function isSitesDefined(sites?: Sites[] | null): sites is Sites[] {
  return !!sites;
}

function isOpsgenieIncluded(productKeys: Product[]) {
  return _.includes(
    _.map(
      productKeys,
      (productKey: Product) => productKey.product === ProductKeys.OPSGENIE
    ),
    true
  );
}

export function isProductIncluded(productKeys: Product[], product: string) {
  return _.includes(
    _.map(productKeys, (productKey: Product) => productKey.product),
    product
  );
}

export function cleanseNonAdminWorkspaces(
  bitbucketWorkspaces: BitbucketWorkspaces
) {
  const purifiedData: BitbucketWorkspace[] = _.filter(
    bitbucketWorkspaces.values,
    (workspace: BitbucketWorkspace) => workspace.permission === 'owner'
  );
  return purifiedData;
}

export const SignUpFormUIStateContext = createContext<WacSignupFormProps>({
  productKeys: [] as Product[],
  bitbucketWorkspaces: {
    size: 0,
    values: [] as BitbucketWorkspace[]
  },
  hideFullName: false,
  showEmail: false,
  userInfo: {
    firstName: '',
    fullName: '',
    lastName: '',
    email: '',
    sites: [] as Sites[],
    isLoggedIn: false,
    extendedTrialOverridableSites: [] as string[],
    accountId: '',
    isNewUser: false
  },
  marketingConsentRequired: false,
  signInAccountUrl: '',
  switchAccountUrl: '',
  originAnalyticsAttributes: {
    signInUser: null,
    switchUser: null
  },
  reCaptchaKey: '',
  devMode: false,
  migrationData: {
    migrationSourceUuid: '',
    migrationUserLimit: 0,
    migrationUserType: '',
    migrationProductName: '',
    migrationSites: [],
    invalidMigration: false,
    claimedMigration: false
  },
  isStaging: false,
  wacEnvUrl: '',
  locale: '',
  templateId: { templateId: '' },
  analyticsTrack: {
    newRelic: {
      action: () => undefined,
      attribute: () => undefined,
      trackAjax: () => undefined
    },
    segment: () => undefined
  },
  formExistingSiteShown: () => undefined,
  deferSiteNameCreation: false,
  defaultErrorType: SubmitErrorType.NO_ERROR,
  enableFreeBreachTest: false,
  simulateFreeBreachState: false,
  spProduct: '',
  hideGreeting: false,
  outlineOnFocus: false,
  fadeOnDisable: false,
  popOverError: false,
  enableFieldProgression: false,
  showWarningIcon: false,
  selectedSiteId: '',
  hideCaptchaEula: false,
  enableResendEmail: false,
  enableCarouselEndCard: false,
  firstImpressionsImageUrls: {
    backgroundImage: '',
    jiraSoftware: {
      heroImage: '',
      socialProofImage: ''
    },
    jiraServiceDesk: {
      heroImage: '',
      socialProofImage: ''
    },
    confluence: {
      heroImage: '',
      socialProofImage: ''
    },
    jiraCore: {
      heroImage: '',
      socialProofImage: ''
    },
    teamCentral: {
      heroImage: '',
      socialProofImage: ''
    },
    jiraProductDiscovery: {
      heroImage: '',
      socialProofImage: ''
    },
    compass: {
      heroImage: '',
      socialProofImage: ''
    },
    fabric: {
      heroImage: '',
      socialProofImage: ''
    },
    beacon: {
      heroImage: '',
      socialProofImage: ''
    },
    jira: {
      heroImage: '',
      socialProofImage: ''
    },
    bitbucket: {
      heroImage: '',
      socialProofImage: ''
    }
  },
  siteRenamePageUrl: '',
  enableSkipRecaptcha: false,
  userOrgInfo: DEFAULT_ORG_RESPONSE,
  hasCcpEntitlements: false,
  isNewCCPSiteForMigration: false
});

export type SignUpFormUIStateProviderProps = PropsWithChildren<{
  readonly signUpFormProps: WacSignupFormProps;
}>;

export function SignUpFormUIStateProvider({
  signUpFormProps,
  children
}: SignUpFormUIStateProviderProps) {
  return (
    <SignUpFormUIStateContext.Provider value={signUpFormProps}>
      {children}
    </SignUpFormUIStateContext.Provider>
  );
}

export type PickableExistingSitesOptions = {
  productKeys: Product[];
  isBundleBBJSW: boolean;
  isBundleDevops: boolean;
  isTeamCentral: boolean;
  isJpd: boolean;
  isCompass: boolean;
  isOpsgenie: boolean;
  isJswJsmBundle: boolean;
  isJswJwmBundle: boolean;
  sitesToFilter: Sites[];
  extendedTrialOverridableSites: string[] | null;
};

export function getPickableExistingSites({
  productKeys,
  isBundleBBJSW = false,
  isBundleDevops = false,
  isTeamCentral = false,
  isJpd = false,
  isCompass = false,
  isOpsgenie = false,
  isJswJsmBundle = false,
  isJswJwmBundle = false,
  sitesToFilter,
  extendedTrialOverridableSites = []
}: PickableExistingSitesOptions): Sites[] {
  // filter user's sites excluding sites with target products being signed up for
  const defaultProductKeys = productKeys.map((product) => product.product);

  // filter user's sites excluding sites with target products being signed up for,
  const targetProductKeys = productKeys.map((product) => product.product);

  const hasExtendedTrialOverride = (cloudId: string): boolean => {
    return (
      (extendedTrialOverridableSites &&
        extendedTrialOverridableSites.includes(cloudId)) ||
      false
    );
  };

  let pickableSites: Sites[] = [];

  if (isBundleBBJSW) {
    // BB/JSW: only filter out SP/OG, not JSW: user must
    // be able to add BB workspace to JSW site)
    const bbJswFilteredProductKeys: string[] = [
      ProductKeys.STATUSPAGE,
      ProductKeys.OPSGENIE
    ];

    // filter out non admin sites for bbjsw
    const adminSites = sitesToFilter.filter((site) => site.adminAccess);

    pickableSites = adminSites.filter((site) => {
      const sitesMatch = site.products.map((product) => {
        return !bbJswFilteredProductKeys.includes(product);
      });
      return !sitesMatch.includes(false);
    });
  } else if (isOpsgenie) {
    // OG: also filter out JSM
    targetProductKeys.push(ProductKeys.JIRA_SERVICE_DESK);
    targetProductKeys.push(ProductKeys.STATUSPAGE);
    pickableSites = sitesToFilter.filter((site) => {
      const sitesMatch = site.products.map((product) => {
        return !targetProductKeys.includes(product);
      });
      return !sitesMatch.includes(false);
    });
  } else if (isBundleDevops) {
    // filter out non admin sites for devops
    const adminSites = sitesToFilter.filter((site) => site.adminAccess);
    targetProductKeys.push(ProductKeys.STATUSPAGE);
    targetProductKeys.push(ProductKeys.OPSGENIE);
    pickableSites = adminSites.filter((site) => {
      return (
        !_.some(site.products, (product: string) =>
          targetProductKeys.includes(product)
        ) || hasExtendedTrialOverride(site.cloudId)
      );
    });
  } else if (isTeamCentral || isJpd || isCompass) {
    // do not filter out sites containing OG or SP
    pickableSites = sitesToFilter.filter((site) => {
      return (
        !_.some(site.products, (product: string) =>
          defaultProductKeys.includes(product)
        ) || hasExtendedTrialOverride(site.cloudId)
      );
    });
  } else if (isJswJsmBundle || isJswJwmBundle) {
    // Setting pickableSites to empty array to disable site picker
    // https://hello.jira.atlassian.cloud/browse/BXPSU-2449
    // https://hello.jira.atlassian.cloud/browse/BXPSU-2533
    pickableSites = [];
  } else {
    // default filtering
    pickableSites = sitesToFilter.filter((site) => {
      return (
        !_.some(site.products, (product: string) =>
          targetProductKeys.includes(product)
        ) || hasExtendedTrialOverride(site.cloudId)
      );
    });
  }

  // remove all sandbox sites
  pickableSites = pickableSites.filter((site) => {
    return !/sandbox-\d{3}/.test(site.displayName);
  });

  return pickableSites;
}

export function removeExistingPremiumJSMSites({
  isJsm,
  isFree,
  pickableSites
}: {
  isJsm: boolean;
  isFree: boolean;
  pickableSites: Sites[];
}) {
  const JSMProductKeys = [
    `${ProductKeys.JIRA_SERVICE_DESK}`,
    `${ProductKeys.JIRA_SERVICE_MANAGEMENT}`
  ];
  if (isJsm && !isFree) {
    pickableSites = pickableSites.filter((site) => {
      const toFilter = site?.productsAndEditions?.filter(
        ({ productKey, productEdition }) => {
          const existingPremiumJSMSite =
            JSMProductKeys.includes(productKey) &&
            productEdition.toLowerCase() === 'premium';
          return existingPremiumJSMSite;
        }
      );

      return !toFilter?.length;
    });
  }

  return pickableSites;
}

export function useSignUpFormUIState() {
  const signUpFormProps = useContext(SignUpFormUIStateContext);

  const { deferredSiteName } = useAppSelector((store) => store);

  const {
    devMode,
    migrationData,
    userInfo,
    bitbucketWorkspaces,
    marketingConsentRequired,
    productKeys,
    isStaging,
    locale,
    deferSiteNameCreation,
    templateId,
    enableResendEmail = false,
    enableCarouselEndCard = false,
    defaultErrorType = SubmitErrorType.NO_ERROR,
    userOrgInfo,
    hasCcpEntitlements,
    isNewCCPSiteForMigration
  } = signUpFormProps;

  const welcomeMessage =
    userInfo && userInfo.firstName
      ? messages.personalizedWelcomeMessage
      : messages.defaultWelcomeMessage;

  const signupSource = new URLSearchParams(window.location.search).get(
    'signupSource'
  );
  const bitbucketPreferred = signupSource === 'bbbundle' ? true : false;

  const initialState: WacSignupFormState = {
    hasCCPEntitlements: hasCcpEntitlements,
    isNewCCPSiteForMigration: isNewCCPSiteForMigration,
    email: null,
    firstName: null,
    lastName: null,
    displayName: null,
    password: null,
    cloudName: null,
    bbName: null,
    dataRegion: 'US',
    products: [],
    consent: {
      site: null,
      locale: null,
      formUrl: null,
      consents: [
        {
          key: 'termsOfService',
          displayedText: messages.eula.defaultMessage,
          granted: true
        },
        {
          key: 'privacyPolicy',
          displayedText: messages.eula.defaultMessage,
          granted: true
        },
        {
          key: 'generalMarketingOptIn',
          displayedText: messages.marketingConsent.defaultMessage,
          granted: false
        }
      ]
    },
    isExistingSitePickerN2E: false,
    isLoggedIn: false,
    selectedSite: null,
    migrationId: null,
    isSubmitting: false,
    isSSOValid: false,
    cloudNameLength: 0,
    isNewUser: false
  };
  const signupOrigin = new OriginTracing({ product: 'wac-sign-up' });
  const [showResendEmail, setShowResendEmail] = useState(false);
  const [showDevopsCarouselEnd, setShowDevopsCarouselEnd] = useState(false);
  const [goToURL, setGoToURL] = useState('');
  const resendEmail = {
    setShowResendEmail,
    enableResendEmail,
    enableCarouselEndCard,
    setShowDevopsCarouselEnd,
    setGoToURL
  };

  const [isBbCopyEnabled, setIsBbCopyEnabled] = useState(false);
  const bbWorkspaces = bitbucketWorkspaces?.values
    ? cleanseNonAdminWorkspaces(bitbucketWorkspaces)
    : [];
  const bbWorkspacesTotal = bbWorkspaces.length || 0;
  let buttonText = messages.buttonText;
  let startButtonText = messages.startButtonText;

  // @todo: refactor to a function
  const isOpsgenie = isOpsgenieIncluded(productKeys);
  const isStatuspage = isProductIncluded(productKeys, ProductKeys.STATUSPAGE);
  const isTeamCentral = isProductIncluded(
    productKeys,
    ProductKeys.TEAM_CENTRAL
  );
  const isJsw = isProductIncluded(productKeys, ProductKeys.JIRA_SOFTWARE);
  const isConfluence = isProductIncluded(productKeys, ProductKeys.CONFLUENCE);
  const isJsm = isProductIncluded(productKeys, ProductKeys.JIRA_SERVICE_DESK);
  const isJwm = isProductIncluded(productKeys, ProductKeys.JIRA_CORE);
  const isJpd = isProductIncluded(
    productKeys,
    ProductKeys.JIRA_PRODUCT_DISCOVERY
  );
  const isCompass = isProductIncluded(productKeys, ProductKeys.COMPASS);
  const isBeacon = isProductIncluded(productKeys, ProductKeys.BEACON);
  const isBundleBBJSW = getIsBundleBbJsw(productKeys);
  const isBitbucket = getIsBitbucket(productKeys);
  const isBundleDevops = getIsBundleDevops(productKeys);
  const isJswJsmBundle = isJsw && isJsm;
  const isJswJwmBundle = isJsw && isJwm;
  const isFree = productKeys.length > 0 && productKeys[0].edition === 'free';
  // suppress User Segmentation Screen for Jira Template Signups
  const hideUserSegmentation = !!(templateId && (isJsw || isJsm || isJwm));

  // BXPSU-1842 logged out email redirect URL
  const [n2nSignInRedirect, setN2NSignInRedirect] = useState('');
  let isSitePickerDisabled = false;
  if (isBundleDevops) {
    // use Devops submit button text
    buttonText = messages.devopsButtonText;
    startButtonText = messages.devopsStartButtonText;

    // Devops EAP only allows creating new sites. Remove this for Devops GA:
    isSitePickerDisabled = true;
  }

  const orgFetchResponse = userOrgInfo ?? DEFAULT_ORG_RESPONSE;

  const orgAdminVortexStatusCounts = useMemo(() => {
    return getOrgAdminVortexStatusCounts(orgFetchResponse.orgList || []);
  }, [orgFetchResponse.orgList]);

  if (isBitbucket) {
    startButtonText = messages.bitbucketSubmitButtonTextMessage;
  }

  let productsExisting: string[] = [];
  if (isBundleBBJSW && bbWorkspacesTotal > 0) {
    productsExisting = [ProductKeys.BITBUCKET];
  }
  if (isBundleBBJSW && userInfo && userInfo.sites) {
    userInfo.sites.map((site: Sites) => {
      productsExisting = [...productsExisting, ...site.products];
    });
  }

  const hideAsterisks = true;
  let pickableSites: Sites[] = [];
  let intialEmail = '';

  if (!marketingConsentRequired) {
    initialState.consent.consents[2].granted = true;
    initialState.consent.consents[2].displayedText = 'general consent implied';
  }

  if (userInfo && userInfo.firstName && userInfo.email) {
    // N2E
    const { firstName, email, lastName } = userInfo;
    initialState.firstName = firstName;
    initialState.lastName = sanitizeLastName(lastName);
    initialState.displayName = `${firstName} ${initialState.lastName}`.trim();
    intialEmail = email;
    initialState.email = email;
    initialState.isLoggedIn = true;
    initialState.migrationId = migrationData
      ? migrationData.migrationSourceUuid
      : null;

    delete initialState.password;

    !isOpsgenie
      ? delete initialState.dataRegion
      : (initialState.dataRegion = 'US');

    if (isSitesDefined(userInfo?.sites) && userInfo.sites.length > 0) {
      if (userInfo.sites.length === 1 && !isBitbucket) {
        initialState.cloudName = userInfo.sites[0].displayName;
        initialState.selectedSite = userInfo.sites[0];
      }

      if (bbWorkspaces.length > 0) {
        initialState.bbName = bbWorkspaces[0].workspace.name;
      }

      pickableSites = getPickableExistingSites({
        productKeys,
        isBundleBBJSW,
        isBundleDevops,
        isTeamCentral,
        isJpd,
        isCompass,
        isOpsgenie,
        isJswJsmBundle,
        isJswJwmBundle,
        sitesToFilter: userInfo.sites,
        extendedTrialOverridableSites: userInfo.extendedTrialOverridableSites
      });

      pickableSites = removeExistingPremiumJSMSites({
        isJsm,
        isFree,
        pickableSites
      });
    } else if (userInfo.isNewUser) {
      initialState.isNewUser = true;
    }
  }

  initialState.consent.locale = locale;
  initialState.consent.site = 'atlassian';
  initialState.consent.formUrl = window.location.href;
  initialState.products = productKeys;

  // if BB preferred then ensure BB is the primary product
  if (bitbucketPreferred && isBundleBBJSW) {
    initialState.products = [...initialState.products].sort((a, b) =>
      (a.product || '').localeCompare(b.product || '')
    );
  }

  initialState.isExistingSitePickerN2E =
    !devMode && (pickableSites.length > 0 || isBitbucket);

  const [signupState, setSignupState] = useState(initialState);
  const [pollingLoaderShown, setPollingLoaderShown] = useState(false);
  const [shouldShowUseExisting, setShouldShowUseExisting] = useState(
    !devMode && !_.isEmpty(pickableSites)
  );
  const [isValidSiteName, setIsValidSiteName] = useState(false);
  const [isValidWorkspaceID, setIsValidWorkspaceID] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<AccountOrg | null>(null);
  const [workspaceIDTakenError, setWorkspaceIDTakenError] = useState(false);
  const [orgProvisioningProgressMessage, setOrgProvisioningProgressMessage] =
    useState(false);
  const [submitError, setSubmitError] = useState(defaultErrorType);
  const [activateProductError, setActivateProductError] = useState(false);
  const [selectedSiteIsInBreachOfFree, setSelectedSiteIsInBreachOfFree] =
    useState(false);
  const [
    disableSignInWhileTestingFreeBreach,
    setDisableSignInWhileTestingFreeBreach
  ] = useState(false);

  useEffect(() => {
    const isNewCCPSiteForMigration =
      initialState.migrationId && !signupState.isExistingSitePickerN2E;

    const hasCCPEntitlement =
      initialState.migrationId &&
      !isBitbucket &&
      signupState.selectedSite &&
      signupState.selectedSite.entitlements &&
      signupState.selectedSite.entitlements.find(
        (entilement: { billingSourceSystem: string }) =>
          entilement.billingSourceSystem === 'CCP'
      );

    setSignupState({
      ...signupState,
      hasCCPEntitlements: !!hasCCPEntitlement,
      isNewCCPSiteForMigration: !!isNewCCPSiteForMigration
    });
  }, [
    signupState.selectedSite,
    isBitbucket,
    initialState.migrationId,
    signupState.isExistingSitePickerN2E
  ]);

  useEffect(() => {
    if (
      isBbCopyEnabled &&
      !signupState.isExistingSitePickerN2E &&
      signupState.cloudName &&
      signupState.bbName !== signupState.cloudName
    ) {
      setSignupState({
        ...signupState,
        bbName: signupState.cloudName || ''
      });
    }
    if (
      isBbCopyEnabled &&
      signupState.isExistingSitePickerN2E &&
      signupState.selectedSite &&
      signupState.bbName !== signupState.selectedSite?.displayName
    ) {
      setSignupState({
        ...signupState,
        bbName: signupState.selectedSite?.displayName || ''
      });
    }
  }, [
    isBbCopyEnabled,
    signupState.cloudName,
    signupState.selectedSite,
    signupState.isExistingSitePickerN2E
  ]);

  useEffect(() => {
    const n2eAvailable = !!(
      !devMode &&
      pickableSites.length > 0 &&
      userInfo &&
      isSitesDefined(userInfo?.sites) &&
      userInfo.sites.length > 0
    );
    setSignupState({
      ...signupState,
      products: productKeys,
      isExistingSitePickerN2E: n2eAvailable,
      // @ts-ignore
      selectedSite: n2eAvailable ? userInfo.sites[0] : null,
      cloudName: n2eAvailable
        ? // @ts-ignore
          userInfo.sites[0].displayName
        : signupState.isExistingSitePickerN2E
        ? ''
        : signupState.cloudName
    });
    setShouldShowUseExisting(!devMode && !_.isEmpty(pickableSites));
  }, [productKeys]);

  const siteNameAfter = useMemo(() => {
    if (isOpsgenie && !isBundleDevops) {
      return '.app.opsgenie.com';
    }
    if (isStaging) {
      return '.jira-dev.com';
    } else {
      return '.atlassian.net';
    }
  }, [signupState.dataRegion]);

  const isValid = useMemo(() => {
    return (
      signupState.email !== null &&
      signupState.email !== '' &&
      ((signupState.cloudName !== null && signupState.cloudName !== '') ||
        deferSiteNameCreation ||
        (deferredSiteName.isEnabled && signupState.cloudNameLength === 0)) &&
      !signupState.isSSOValid
    );
  }, [signupState]);

  const isValidChooseSignup = useMemo(() => {
    return (
      signupState.email !== null &&
      signupState.email !== '' &&
      !signupState.isSSOValid
    );
  }, [signupState]);

  const isValidN2E = useMemo(() => {
    const isUserInfoValid =
      signupState.email !== null &&
      signupState.email !== '' &&
      signupState.firstName !== null &&
      signupState.firstName !== '' &&
      signupState.lastName !== null &&
      signupState.lastName !== '' &&
      ((userInfo && userInfo.email !== '' && userInfo.email !== null) ||
        (signupState.password !== null && signupState.password !== ''));
    const isValidName = isBitbucket
      ? isValidWorkspaceID
      : isValidSiteName &&
        signupState.cloudName !== null &&
        signupState.cloudName !== '';
    const isAccountOrgSelected = isBitbucket
      ? selectedOrg !== null || orgAdminVortexStatusCounts.ENABLED === 0
      : true;
    return (
      isUserInfoValid &&
      !signupState.isSSOValid &&
      isValidName &&
      isAccountOrgSelected
    );
  }, [signupState, isValidSiteName, isValidWorkspaceID, selectedOrg]);

  useEffect(() => {
    if (
      orgAdminVortexStatusCounts.ENABLED === 1 &&
      orgAdminVortexStatusCounts.DISABLED === 0 &&
      orgFetchResponse.orgList
    ) {
      setSelectedOrg(orgFetchResponse.orgList[0]);
    }
  }, [orgAdminVortexStatusCounts]);

  return {
    signupState,
    setSignupState,
    pollingLoaderShown,
    setPollingLoaderShown,
    showResendEmail,
    setShowResendEmail,
    intialEmail,
    showDevopsCarouselEnd,
    setShowDevopsCarouselEnd,
    goToURL,
    setGoToURL,
    resendEmail,
    isBbCopyEnabled,
    setIsBbCopyEnabled,
    bbWorkspaces,
    bbWorkspacesTotal,
    isOpsgenie,
    isStatuspage,
    isTeamCentral,
    isBundleBBJSW,
    isBundleDevops,
    isCompass,
    isBeacon,
    isBitbucket,
    isJpd,
    isFree,
    buttonText,
    startButtonText,
    isSitePickerDisabled,
    productsExisting,
    hideAsterisks,
    pickableSites,
    shouldShowUseExisting,
    isValidSiteName,
    setIsValidSiteName,
    submitError,
    setSubmitError,
    activateProductError,
    setActivateProductError,
    selectedSiteIsInBreachOfFree,
    setSelectedSiteIsInBreachOfFree,
    disableSignInWhileTestingFreeBreach,
    setDisableSignInWhileTestingFreeBreach,
    siteNameAfter,
    isValid,
    isValidChooseSignup,
    isValidN2E,
    initialState,
    welcomeMessage,
    signUpFormProps,
    hideUserSegmentation,
    signupOrigin,
    n2nSignInRedirect,
    setN2NSignInRedirect,
    isJsw,
    isConfluence,
    isValidWorkspaceID,
    setIsValidWorkspaceID,
    orgFetchResponse,
    selectedOrg,
    setSelectedOrg,
    workspaceIDTakenError,
    setWorkspaceIDTakenError,
    orgAdminVortexStatusCounts,
    orgProvisioningProgressMessage,
    setOrgProvisioningProgressMessage
  };
}
